import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useViewerHasAtLeastCustomerSupportRights } from "@/lib/rights/use-viewer-has-right";
import ResolutionSelect from "@/pages/(app)/returns/refund/validate/resolution-select";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context";
import { useLineItemIsLoading } from "@/pages/(app)/returns/utils/line-items-loading-state-context";
import ArticlePrice from "@/pages/(app)/returns/view/components/return-articles/article-price";
import ReturnArticleResolution from "@/pages/(app)/returns/view/components/return-articles/return-article-resolution";
import { cn } from "@/utils/cn";
import {
  ProductVariantDto,
  ReturnRequestLineItemDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Edit2Icon, ExternalLink, Loader2 } from "lucide-react";
import { ArticleItemReason } from "./article-item-reason";
import {
  ArticleItemStatus,
  ArticleItemStatusProps,
} from "./article-item-status";
import { ArticleItemSubline } from "./article-item-subline";
import { ArticleQuantity } from "./article-quantity";
import useViewerRole from "@/lib/rights/use-viewer-role";
import EditArticleItemDialog from "./edit-article-item-dialog";

type ArticleItemProps = {
  lineItem: ReturnRequestLineItemDto;
  maxQuantity?: number;
  status?: ArticleItemStatusProps["status"];
  quantity: number;
  onChangeQuantity?: (quantity: number) => void;
  onChangeResolution?: (
    resolution: ReturnRequestLineItemDto["resolution"],
  ) => void;
  onChangeVariant?: (variant: ProductVariantDto) => void;
  hideReason?: boolean;
  hideStatus?: boolean;
  hideComment?: boolean;
  hideImages?: boolean;
  hideVariant?: boolean;
  hideRejectReason?: boolean;
  hideResolution?: boolean;
  showNewArticleCommandCode?: boolean;
  disabled?: boolean;
  disabledReason?: React.ReactNode;
};

export function ArticleItem({
  lineItem,
  quantity,
  maxQuantity,
  status,
  onChangeVariant,
  onChangeQuantity,
  onChangeResolution,
  hideVariant,
  hideResolution,
  hideImages,
  hideReason,
  hideRejectReason,
  hideStatus,
  hideComment,
  disabled,
  disabledReason,
}: ArticleItemProps) {
  const { data: shop } = useCurrentShopSuspense();
  const returnRequest = useCurrentReturnRequest();
  const currency = returnRequest.financial_informations.refund.currency;
  const t = useTranslation();
  const loading = useLineItemIsLoading(lineItem.line_item_id);

  const hasRightsToEditResolution = useViewerHasAtLeastCustomerSupportRights();
  const canEditResolution = hasRightsToEditResolution && !!onChangeResolution;

  const newOrders = lineItem?.merchant_informations.filter(
    (mi) => mi.result && mi.result.newOrderId,
  );

  const borderLeft = useBorderLeft(status);

  return (
    <div
      className={cn(
        "relative flex border-l-4 group",
        borderLeft,
        loading && "pointer-events-none",
      )}
    >
      <EditArticleItemDialog
        lineItem={lineItem}
        returnRequestId={returnRequest.id}
      >
        <div className="absolute top-2 right-2 p-2 bg-white hover:bg-gray-50 rounded-md shadow-sm cursor-pointer hidden group-hover:block transition-all duration-200 ease-in-out">
          <Edit2Icon className="size-4" />
        </div>
      </EditArticleItemDialog>
      {loading && (
        <div className="flex absolute items-center gap-2 top-4 right-4 text-orange-400">
          <span className="text-xs font-semibold">{t("misc.loading")}</span>
          <Loader2 className="size-3 animate-spin" />
        </div>
      )}
      <div
        className={cn(
          "flex-1 transition-all duration-200 ease-in-out bg-transparent hover:bg-muted/40 rounded-lg flex gap-4 m-1 p-2",
          loading && "bg-orange-400/5",
        )}
      >
        <div className="h-full">
          <img
            src={lineItem.image ?? "https://placehold.co/600x400"}
            alt="return"
            className="h-full max-h-16 aspect-[9/12] rounded-lg object-cover"
          />
        </div>
        <div className="flex-1 flex flex-col justify-between">
          <div className="flex flex-col">
            <span className="font-semibold text-sm inline-flex items-center gap-2 flex-wrap">
              <span>{lineItem.title}</span>
              {!hideResolution && (
                <ResolutionSelect
                  value={lineItem.resolution}
                  editable={canEditResolution}
                  onChange={onChangeResolution}
                >
                  <ReturnArticleResolution
                    editable={canEditResolution}
                    resolution={lineItem.resolution}
                  />
                </ResolutionSelect>
              )}
            </span>
            <ArticleItemSubline
              lineItem={lineItem}
              onChangeVariant={onChangeVariant}
              hideVariant={hideVariant}
              hideReturnReason={hideReason}
              hideImages={hideImages}
              hideRejectReason={hideRejectReason}
              hideComment={hideComment}
            />

            <ArticleItemReason
              lineItem={lineItem}
              hideComment={hideComment}
              hideImages={hideImages}
              hideReason={hideReason}
            />

            <span className={cn("text-xs", !newOrders.length && "hidden")}>
              Nouvelles commandes:{" "}
              {lineItem?.merchant_informations
                .filter((mi) => mi.result && mi.result.newOrderId)
                .map((mi) => (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`https://${shop.shopifyUrl}/admin/orders/${mi.result?.newOrderId}`}
                    className="text-xs truncate max-w-[200px] inline-flex items-center underline font-medium gap-2"
                  >
                    {mi.result?.newOrderName}{" "}
                    <ExternalLink className="size-3" />
                  </a>
                ))}
            </span>
          </div>
          {/* <div>
            {showNewArticleCommandCode && (
              <NewArticleCommandCode
                shopifyUrl={shop.shopifyUrl}
                lineItem={lineItem}
              />
            )}
          </div> */}
        </div>
        <div className="h-full flex justify-between items-end flex-col">
          <div className={cn(loading && "opacity-0")}>
            <span className="text-md inline-flex items-baseline gap-1">
              <ArticlePrice
                paidPrice={lineItem.paidPrice}
                price={lineItem.price}
                quantity={quantity}
                currency={currency}
              />
            </span>
          </div>
          {!hideStatus && status && <ArticleItemStatus status={status} />}
          <ArticleQuantity
            quantity={quantity}
            onChange={onChangeQuantity}
            productPrice={+lineItem.price}
            maxQuantity={maxQuantity ?? 0}
            disabled={disabled}
            disabledReason={disabledReason}
          />
        </div>
      </div>
    </div>
  );
}

function useBorderLeft(status?: ArticleItemStatusProps["status"]) {
  const role = useViewerRole() === "logistician" ? "logistician" : "merchant";

  if (!status) return "border-l-transparent";

  if (role === "merchant") {
    if (status === "treated-by-merchant") {
      return "border-l-green-400";
    }
    if (status === "validated-by-logistician") {
      return "border-l-orange-400";
    }
    if (
      status === "rejected-by-merchant" ||
      status === "rejected-by-logistician"
    ) {
      return "border-l-destructive";
    }
  } else {
    if (
      status === "treated-by-merchant" ||
      status === "rejected-by-merchant" ||
      status === "ready-for-merchant" ||
      status === "validated-by-logistician"
    ) {
      return "border-l-green-400";
    }
    if (status === "rejected-by-logistician") {
      return "border-l-destructive";
    }
  }
  return "border-l-transparent";
}
